/* eslint-disable no-invalid-this */
import React from 'react';
import {
  IRouteScreenProps,
  ISupplierUserProfile,
  ISupportCustomer,
} from 'constants/interfaces';
import {
  apiFarmerCreate,
  apiFarmerInvite,
  apiGetSupportCustomers,
} from 'constants/Api';
import {Button, Snackbar, Typography} from '@material-ui/core';
import {connect} from 'react-redux';
import {Route} from 'react-router-dom';
import {NavigationService} from 'services/index';
import AddIcon from '@material-ui/icons/Add';
import {ESupplierUserPermissions} from 'constants/index';
import {PermissionsService} from 'services/PermissionsService';
import {ChangeAccountManagerDialog} from 'components/Farmer/ChangeAccountManagerDialog';
import {KhulaSupplierTable, Row} from 'components/Table/KhulaSupplierTable';
import {
  customerTableDefaultSortField,
  customerTableFilters,
  customerTableHeaders,
  customerTableRows,
} from './CustomerTableSetup';

import InviteCodePanel from './InviteCodePanel/InviteCodePanel';
import InviteFarmersDialog, {
  FarmerDraft,
} from './InviteFarmers/InviteFarmersDialog';
import {v4 as uuid} from 'uuid';

type CustomersProps = IRouteScreenProps & {
  user: ISupplierUserProfile;
};

interface CustomersState {
  isLoading: boolean;
  isRefreshing: boolean;
  showRefreshedSnackbar: boolean;
  customers: ISupportCustomer[];
  showInviteDialog: boolean;
  isInviting: boolean;
  showChangeAccountManagerDialog: boolean;
  customerToEdit?: ISupportCustomer;
  showNotAcceptedSnackbar: boolean;
}

class Customers extends React.Component<CustomersProps, CustomersState> {
  state: CustomersState = {
    /* data */
    isLoading: true,
    isRefreshing: false,
    showRefreshedSnackbar: false,
    customers: [],
    /* dialogs */
    showInviteDialog: false,
    isInviting: false,
    showChangeAccountManagerDialog: false,
    customerToEdit: undefined,
    showNotAcceptedSnackbar: false,
  };

  /* ───────────────── life-cycle ───────────────── */
  componentDidMount() {
    this.fetchCustomers();
  }

  /* ───────────────── data fetch helpers ───────────────── */
  fetchCustomers = () => {
    this.setState({isLoading: true}, () => {
      apiGetSupportCustomers()
        .then((customers) => this.setState({customers, isLoading: false}))
        .catch((err) => {
          alert('Error loading customers');
          console.error(err);
          this.setState({customers: [], isLoading: false});
        });
    });
  };

  refreshCustomers = () => {
    this.setState({isRefreshing: true}, () => {
      apiGetSupportCustomers()
        .then((customers) =>
          this.setState({
            customers,
            isRefreshing: false,
            showRefreshedSnackbar: true,
          }),
        )
        .catch((err) => {
          alert('Error refreshing customers');
          console.error(err);
          this.setState({customers: [], isRefreshing: false});
        });
    });
  };

  /* ───────────────── navigation helpers ───────────────── */
  openCustomerDetail = (cust: ISupportCustomer) => {
    if (!cust.farmerAccepted && !this.props.user.isKhulaAdmin) {
      this.setState({showNotAcceptedSnackbar: true});
      return;
    }
    NavigationService.navigate(`/customers/dashboard/${cust.farmer.userId}`);
  };

  handleInviteFarmers = async (
    existingFarmerEmails: string[],
    newFarmersToCreate: FarmerDraft[],
  ) => {
    this.setState({isInviting: true});
    const createNewFarmers = newFarmersToCreate.map((submission) =>
      apiFarmerCreate({
        username: submission.email,
        password: uuid(),
        name: submission.firstName,
        surname: submission.lastName,
        email: submission.email,
        phone: submission.phone,
        registrationId: '',
        inviteCode: this.props.user.invitationCode,
        farmName: submission.company,
      }).catch((error) => {
        alert(
          `Failed to add farmer: ${submission.email}. Please try again later.`,
        );
        console.log(`Failed to create farmer: ${submission.email}`, error);
      }),
    );
    await Promise.all(createNewFarmers);

    if (existingFarmerEmails.length > 0) {
      await apiFarmerInvite(existingFarmerEmails).catch((error) => {
        alert(
          'Failed to invite some existing farmers. Please try again later.',
        );
        console.log('Failed to invite existing farmers', error);
      });
    }

    this.setState({isInviting: false, showInviteDialog: false});
    this.refreshCustomers();
  };

  /* ───────────────── render ───────────────── */
  render() {
    const {
      isLoading,
      isRefreshing,
      customers,
      showRefreshedSnackbar,
      showInviteDialog,
      showChangeAccountManagerDialog,
      customerToEdit,
    } = this.state;

    const permissions = new PermissionsService(this.props.user);
    const canAdd = permissions.hasPermission(
      ESupplierUserPermissions.CUSTOMER_ADD,
    );

    /* ───── derive role safely ───── */
    const role = (permissions as unknown as {role?: string}).role;
    const isClient = role === 'CLIENT';

    const toolbarActions = canAdd ? (
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        size="medium"
        className="add-customer-button"
        onClick={() => this.setState({showInviteDialog: true})}
      >
        {isClient ? 'Invite farmer' : 'Add farmer'}
      </Button>
    ) : undefined;

    return (
      <div className="customers-container">
        <InviteCodePanel />

        <Typography variant="h4" className="page-title">
          Farmers
        </Typography>
        <Typography variant="body1" className="page-subtitle">
          List of your farmers
        </Typography>

        <Route
          render={() => (
            <div className="customers-section">
              <KhulaSupplierTable
                headers={customerTableHeaders(this.props.user.isKhulaAdmin)}
                rows={customerTableRows(
                  customers,
                  this.props.user.isKhulaAdmin,
                )}
                defaultSortField={customerTableDefaultSortField}
                filtersGroups={customerTableFilters}
                isLoading={isLoading}
                isRefreshing={isRefreshing}
                onRefresh={this.refreshCustomers}
                toolbarActions={toolbarActions}
                // searchPlaceholder="Search farmers"
                onRowSelect={(row: Row) => {
                  const customer = customers.find((c) => c.farmer.id == row.id);
                  if (customer) this.openCustomerDetail(customer);
                }}
              />

              <Snackbar
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                open={showRefreshedSnackbar}
                onClose={() => this.setState({showRefreshedSnackbar: false})}
                autoHideDuration={3000}
                message="Customers refreshed"
              />

              <ChangeAccountManagerDialog
                user={this.props.user}
                customer={customerToEdit}
                open={showChangeAccountManagerDialog}
                onClose={() =>
                  this.setState({showChangeAccountManagerDialog: false})
                }
                onSuccess={this.refreshCustomers}
              />
              <InviteFarmersDialog
                open={showInviteDialog}
                onClose={() => this.setState({showInviteDialog: false})}
                onSubmit={(existingFarmerEmails, newFarmerToCreate) =>
                  this.handleInviteFarmers(
                    existingFarmerEmails,
                    newFarmerToCreate,
                  )
                }
                isSubmitting={this.state.isInviting}
              />
              <Snackbar
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                open={this.state.showNotAcceptedSnackbar}
                onClose={() => this.setState({showNotAcceptedSnackbar: false})}
                autoHideDuration={3000}
                message="Customer hasn't accepted yet."
              />
            </div>
          )}
        />
      </div>
    );
  }
}

/* ───────────────── redux wire-up ───────────────── */
const mapStateToProps = (state: any) => ({user: state.user});
export default connect(mapStateToProps)(Customers);
